import { useState, useEffect } from 'react';
import fetchApi from '../../utils/api/fetchApi';
import { Menu, MenuProps, Tooltip } from 'antd'; // 引入 Tooltip
import systematic from '../../config/systematic';
import AntdIcon from './AntdIcon';
import { Link } from 'react-router-dom';
import antMessage from '../../utils/extend/AntdNotification';
type MenuItem = Required<MenuProps>['items'][number];

interface licenseGroup {
  id: Number,
  licenseGroupName: String,
  icon: String,
  licenseGroupSort: number,   // 组的排序字段
  licenses: license[],
};

interface license {
  id: Number,
  licenseName: String,
  icon: String,
  pageUrl: String,
  parentId: Number | null,    // 父级 id
  sort: number,               // 原 licenseSort 改名
  children?: license[],      // 子节点（后端 buildTree 生成）
};

interface httpData {
  success: boolean,
  data: licenseGroup[];
};

/** 文本单行截断并显示省略号的通用样式 */
const textEllipsisStyle: React.CSSProperties = {
  display: 'inline-block',
  width: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
};

/**
 * 判断 pageUrl 是否有效
 *  - null / undefined / '' / 空白字符串 都视为无效
 */
const isValidPageUrl = (url: any): boolean =>
  typeof url === 'string' && url.trim().length > 0;

/**
 * 把 license 节点递归转成 Antd MenuItem
 *  - 有 children：继续递归
 *  - pageUrl 有效：渲染 Link，可跳转
 *  - pageUrl 无效：渲染纯文本，不可跳转（避免 <Link to={null}> 崩溃）
 */
const toMenuItem = (item: license): MenuItem => {
  // 子节点：递归转换 + 按 sort 排序
  const children = Array.isArray(item.children) && item.children.length
    ? item.children
      .slice()
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
      .map(toMenuItem)
    : undefined;

  // pageUrl 有效才包 Link，否则渲染纯文本
  const labelContent = isValidPageUrl(item.pageUrl)
    ? <Link to={item.pageUrl as string} style={textEllipsisStyle}>{item.licenseName}</Link>
    : <span style={textEllipsisStyle}>{item.licenseName}</span>;

  return {
    // 使用 Tooltip 包裹，placement="right" 让提示框显示在右侧，避免被侧边栏遮挡
    label: (
      <Tooltip title={item.licenseName as string} placement="right" mouseEnterDelay={0.5}>
        {labelContent}
      </Tooltip>
    ),
    key: String(item.id),
    icon: <AntdIcon iconName={item.icon} />,
    children,
  };
};

/** 从 sessionStorage 安全读取 openKeys 数组 */
const readOpenKeys = (): string[] => {
  try {
    const raw = sessionStorage.getItem("latestOpenKey");
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [raw];
  } catch {
    return [];
  }
};

const useSetMenu = (resAll: httpData) => {
  //子菜单
  const [selectedKeys, setSelectedKeys] = useState<any>(sessionStorage.getItem("MenuItemKey"))

  //菜单（改成数组，支持多层展开）
  const [openKeysState, setOpenKeysState] = useState<string[]>(readOpenKeys());

  const onOpenChange = (openKeys: string[]) => {
    // 直接存整个数组，保留所有已展开层级
    setOpenKeysState(openKeys);
    sessionStorage.setItem("latestOpenKey", JSON.stringify(openKeys));
  };

  const clickMenuItem = (MenuItem: any) => {
    //存储在sessionStorage上
    sessionStorage.setItem("MenuItemKey", MenuItem.keyPath[0])
    setSelectedKeys(MenuItem.keyPath[0])
    // 不再覆盖 openKeys —— 展开状态交给 onOpenChange 统一管理
  };

  if (resAll === undefined) { return }
  let resData = resAll.data;

  //修改数据
  //  - 组按 licenseGroupSort 排序
  //  - 组内 licenses 递归转 MenuItem（子节点在 toMenuItem 内部处理）
  let newData = resData
    .slice()
    .sort((a: licenseGroup, b: licenseGroup) => (a.licenseGroupSort ?? 0) - (b.licenseGroupSort ?? 0))
    .map((item: licenseGroup, key: number): MenuItem => {
      return {
        // 组级菜单名称同样使用 Tooltip 和截断样式
        label: (
          <Tooltip title={item.licenseGroupName as string} placement="right" mouseEnterDelay={0.5}>
            <span style={textEllipsisStyle}>{item.licenseGroupName}</span>
          </Tooltip>
        ),
        key: `group-${item.id ?? key}`,   // 用 id 做 key，避免索引漂移
        icon: <AntdIcon iconName={item.icon} />,
        children: (item.licenses ?? [])
          .slice()
          .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
          .map(toMenuItem),
      }
    })

  let recordsComponent = (
    <Menu
      theme="dark"
      mode="inline"
      openKeys={openKeysState}          // ← 数组，支持多层
      items={newData as MenuItem[]}
      selectedKeys={[selectedKeys]}
      onClick={clickMenuItem}
      onOpenChange={e => onOpenChange(e)}
    >

    </Menu>)
  return recordsComponent;
};

const SiderMenu = () => {
  const [records, setRecords] = useState<httpData>();
  /** 后端数据defaultService */
  const defaultService: any = systematic.bmService;
  useEffect(() => {
    //web 方式
    const fetchData = async () => {
      fetchApi.GET(defaultService,
        {
          apiId: 'licenseGroup',
          apiExtend: 'siderMenu',
          // apiVariable: 'webAuth'
        }).then(res => {
          if (res.data.length === 0) {
            antMessage('error', "错误", "无菜单数据/无菜单权限/token过期")
            setTimeout(() => {
              localStorage.clear();
              window.location.href = "/login"
            }, 2000)
          }
          setRecords(res);
        }).catch(err => {
          if (err.message === "Invalid/Expired 无效/过期 token") {
            antMessage('error', "错误", err.message)
            setTimeout(() => {
              localStorage.clear();
              window.location.href = "/login"
            }, 2000)
          }
        })
    };
    fetchData();
  }, []);

  return (
    <div>
      {useSetMenu(records as httpData)}
    </div>
  )
}

export default SiderMenu;